<template>
  <section class="relative bg-[#F7F7F7] text-gray-900 py-20 px-6 md:px-20">
    <!-- Decorative corners -->
    <div class="absolute top-4 right-4">
      <CornerDecoration />
    </div>
    <div class="absolute bottom-4 left-4">
      <CornerDecoration />
    </div>

    <!-- Section heading -->
    <div class="relative max-w-4xl mx-auto text-center">
      <div class="absolute top-[-7px] left-[-14px] flex flex-col">
        <div class="w-4 h-0.5 bg-[#E9A53D]"></div>
        <div class="w-0.5 h-4 bg-[#E9A53D]"></div>
      </div>
      <div class="absolute bottom-[0px] right-[-14px] flex flex-col items-end">
        <div class="w-0.5 h-4 bg-[#E9A53D]"></div>
        <div class="w-4 h-0.5 bg-[#E9A53D]"></div>
      </div>

      <h2 class="text-5xl font-bold text-[#2E2E2E]">
        What do we <span class="text-[#E9A53D]">specialize</span> in? Renovations and interior finishing
      </h2>
      <p class="mt-4 text-lg text-[#555]">
        A professional design and construction studio based in Poznań, offering comprehensive building services, interior design, and renovations.
      </p>
      <div class="w-48 h-[2px] bg-[#E9A53D] mx-auto mt-4"></div>
    </div>

    <!-- Company description -->
    <div class="max-w-screen-lg mx-auto text-center mt-16 px-4">
      <p class="text-xl text-[#444] leading-relaxed">
        For years, we've been delivering innovative architectural solutions. Our passion for design is combined with functionality, modern style, and top-quality craftsmanship. Together, we’ll create the space you've always dreamed of.
      </p>
    </div>

    <!-- Services list -->
    <section class="mt-12 grid grid-cols-1 md:grid-cols-2 gap-12">
      <article
          v-for="(service, index) in services"
          :key="index"
          ref="serviceElements"
          class="flex flex-col items-center text-center opacity-0 transform translate-y-8 transition-all duration-700"
          :class="{ 'opacity-100 translate-y-0': service.visible }"
      >
        <div class="relative w-full h-72">
          <img
              :src="service.image"
              :alt="`Service: ${service.title} – ProjectSpot, Poznań`"
              class="w-full h-full object-cover rounded-sm shadow-md"
          />
        </div>
        <h3 class="text-2xl font-semibold text-[#2E2E2E] mt-6">
          {{ service.title }}
        </h3>
        <p class="text-[#555] mt-3">{{ service.description }}</p>
      </article>
    </section>
  </section>
</template>

<script>
import { onMounted, ref, reactive } from 'vue'

export default {
  name: 'ServicesSection',
  components: {
    CornerDecoration: {
      template: `
        <div class="relative w-10 h-10 border-2 border-[#E9A53D]">
          <div class="absolute top-[-7px] left-[-7px] flex flex-col">
            <div class="w-4 h-0.5 bg-[#E9A53D]"></div>
            <div class="w-0.5 h-4 bg-[#E9A53D]"></div>
          </div>
          <div class="absolute bottom-[-7px] right-[-7px] flex flex-col items-end">
            <div class="w-0.5 h-4 bg-[#E9A53D]"></div>
            <div class="w-4 h-0.5 bg-[#E9A53D]"></div>
          </div>
        </div>
      `
    }
  },
  setup() {
    const serviceElements = ref([])
    const services = reactive([
      {
        title: 'Finishing and Renovation Works – Poznań',
        description: 'Comprehensive interior finishing, apartment and house renovations in Poznań – high quality, punctuality, and experience.',
        image: '/images/components/image1.jpg',
        visible: true
      },
      {
        title: 'Commercial Spaces and Offices – Design and Execution',
        description: 'We design and furnish office and commercial interiors – functionality and aesthetics tailored for business.',
        image: '/images/components/image2.jpeg',
        visible: true
      },
      {
        title: 'Construction Project Coordination – Poznań',
        description: 'We provide professional supervision and coordination of construction investments, minimizing risks and delays.',
        image: '/images/components/image3.jpg',
        visible: true
      },
      {
        title: 'Interior Design and Architecture – Poznań',
        description: 'We create unique home and interior designs – from concept to execution, maintaining the highest standards.',
        image: '/images/components/image4.jpg',
        visible: true
      }
    ])

    const revealOnScroll = () => {
      const observer = new IntersectionObserver(
          entries => {
            entries.forEach((entry, index) => {
              if (entry.isIntersecting) {
                services[index].visible = true
                observer.unobserve(entry.target)
              }
            })
          },
          { threshold: 0.1 }
      )

      serviceElements.value.forEach(el => {
        if (el) observer.observe(el)
      })
    }

    onMounted(() => {
      revealOnScroll()
    })

    return {
      services,
      serviceElements
    }
  }
}
</script>

<style scoped>
.opacity-0 {
  opacity: 0;
}
.opacity-100 {
  opacity: 1;
}
.translate-y-8 {
  transform: translateY(32px);
}
.translate-y-0 {
  transform: translateY(0);
}
</style>
